import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Loader } from '../Loader';
import { SubscriptionPlanSelectorV7 } from '../SubscriptionPlanSelectorV7';
import { RiskConcernSummaryV2 } from '../RiskConcernSummaryV2';
import { Faq } from '../Faq';
import { FeedbackListV2 } from '../FeedbackListV2';
import { Checkout } from '../Checkout';
import { Icon } from '../Icon';
import { EmojiIcon } from '../EmojiIcon';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import { getChapterLabel, getChapterCategory } from '../../shared/chapter-answer-mapper';
import { calculateProfileScore } from '../../shared/profile-score-helpers';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN,
    MK_TWENTY_FOUR_WEEKS_8999_TWENTY_FOUR_WEEKS_4999_INTRO_PLAN,
    MK_TWENTY_FOUR_WEEKS_8999_WITHOUT_TRIAL_PLAN
} from '../../../lib/shared/subscription-plans';
import beforeComparisonImageSrc from '../../assets/images/sale/memory-sale-before-book.png';
import afterComparisonImageSrc from '../../assets/images/sale/memory-sale-after-book.png';
import styles from './SaleV29.css';

const cx = classnames.bind(styles);

const PEN_COMPARISON_ROWS = [
    'Always knowing what comes next',
    'No blank page to face',
    'No pressure to remember everything at once',
    'A digital life book that grows with every answer'
];

const PLAN_FEATURES = [
    {
        icon: 'question',
        title: 'One question at a time',
        description: 'Answer one at a time, in the order you choose. Never a blank page.'
    },
    {
        icon: 'speaking-head',
        title: 'Speak or type',
        description: 'Speak and we’ll transcribe it, or type when you prefer. Your voice is never stored.'
    },
    {
        icon: 'books',
        title: 'Your chapters come back written',
        description: 'Your answers are turned into a finished piece you can keep or send to someone.'
    },
    {
        icon: 'lock',
        title: 'Private by default',
        description: 'Your stories stay private unless you choose to share them.'
    }
];

const plansWithoutPromo = [MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN, MK_TWENTY_FOUR_WEEKS_8999_WITHOUT_TRIAL_PLAN];
const plansWithMainPromo = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_TWENTY_FOUR_WEEKS_8999_TWENTY_FOUR_WEEKS_4999_INTRO_PLAN];
const popularPlans = [MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN];

function SaleV29({
    data,
    initialPrelander,
    expiryTimestamp,
    onExpirePromo = () => {},
    isExpired = true,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks
}) {
    const { plans, isLoading } = useSubscriptionPlans([], isExpired);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);
    const chapterLabel = getChapterLabel(data.userResponses);
    const profileScore = calculateProfileScore(data.userResponses);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { chapter: chapterLabel });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        const planOrder = !isExpired && data.promoType === FP_PROMO_MAIN_TYPE ? plansWithMainPromo : plansWithoutPromo;

        return planOrder
            .map(plan => plans.find(item => item.plan === plan))
            .filter(Boolean)
            .map(plan => ({ ...plan, isPopular: popularPlans.includes(plan.plan) }));
    };

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans, isExpired]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses)
                }
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses)
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <div className={styles.fixedModal}>
                <Checkout
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                    termsOfUseLink={legalLinks.termsOfUse}
                    defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                    isWalletEnabled
                />
            </div>
        );
    }

    const renderPlans = (hasPaymentInformation = true) => {
        const newPlans = getPlans();

        return (
            <>
                {isLoading
                    ? <Loader className={styles.loader} variant='secondary' />
                    : (
                        <SubscriptionPlanSelectorV7
                            onSubmit={handleSubscribe}
                            selectedSubscriptionData={activePlanData}
                            selectSubscription={setActivePlanData}
                            isPending={isPending}
                            plans={newPlans}
                            expiryTimestamp={expiryTimestamp}
                            onExpirePromo={onExpirePromo}
                            isExpired={isExpired}
                            className={styles.blockWithIndent}
                            hasPaymentInformation={hasPaymentInformation}
                        />
                    )
                }
            </>
        );
    };

    return (
        <div className={styles.root}>
            <section className={styles.comparison}>
                <div className={styles.comparisonTop}>
                    <header className={styles.comparisonHeader}>
                        <span>Right now</span>
                        <Icon name='chevronRightDoubleDark' />
                        <span>with Boundly</span>
                    </header>
                    <div className={styles.comparisonImages}>
                        <img
                            src={beforeComparisonImageSrc}
                            alt='Before Boundly'
                        />
                        <img
                            src={afterComparisonImageSrc}
                            alt='With Boundly'
                        />
                    </div>
                </div>
                <div className={styles.comparisonMetrics}>
                    <div className={styles.metricColumn}>
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>How your life looks to you</p>
                            <p className={styles.metricValue}>Ordinary</p>
                            <div className={styles.progressBar}>
                                <div className={cx('progressFill', 'danger', 'fillShort')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>The meaningful details</p>
                            <p className={styles.metricValue}>Easy to lose</p>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Your memories</p>
                            <p className={styles.metricValue}>Scattered</p>
                            <div className={styles.segmentBar}>
                                <div className={cx('segment', 'segmentFilled', 'danger')} />
                                <div className={styles.segment} />
                                <div className={styles.segment} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.verticalDivider} />
                    <div className={styles.metricColumn}>
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>How your life looks to you</p>
                            <p className={styles.metricValue}>Rich</p>
                            <div className={styles.progressBar}>
                                <div className={cx('progressFill', 'success', 'fillLong')} />
                            </div>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>The meaningful details</p>
                            <p className={styles.metricValue}>In your words</p>
                        </div>
                        <div className={styles.metricRowDivider} />
                        <div className={styles.metricBlock}>
                            <p className={styles.metricName}>Your memories</p>
                            <p className={styles.metricValue}>Finally together</p>
                            <div className={styles.segmentBar}>
                                <div className={cx('segment', 'segmentFilled', 'success')} />
                                <div className={cx('segment', 'segmentFilled', 'success')} />
                                <div className={cx('segment', 'segmentFilled', 'success')} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
            <div className={styles.storyIntro}>
                <h2 className={styles.storyTitle}>
                    Your <em>Life Story Plan</em> is ready!
                </h2>
                <RiskConcernSummaryV2 score={profileScore} chapterLabel={chapterLabel} spriteVersion='v51' />
            </div>
            <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
            {renderPlans()}
            <div className={styles.planFeatures}>
                <h2 className={styles.planFeaturesHeading}>What&apos;s in your plan</h2>
                <ul className={styles.planFeaturesList}>
                    {PLAN_FEATURES.map(feature => (
                        <li key={feature.title} className={styles.planFeatureRow}>
                            <EmojiIcon name={feature.icon} version='v51' className={styles.planFeatureIcon} />
                            <div className={styles.planFeatureCopy}>
                                <p className={styles.planFeatureTitle}>{feature.title}</p>
                                <p className={styles.planFeatureDescription}>{feature.description}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            <div className={styles.penComparison}>
                <div className={styles.penComparisonHeader}>
                    <h2 className={styles.penComparisonTitle}>With a plan vs. on your own</h2>
                </div>
                <div className={styles.table}>
                    <div className={styles.tableHead}>
                        <span className={styles.tableHeadSpacer} />
                        <span className={styles.tableHeadCell}>On your<br />own</span>
                        <span className={styles.tableHeadCell}>With a<br />plan</span>
                    </div>
                    <ul className={styles.tableBody}>
                        {PEN_COMPARISON_ROWS.map(row => (
                            <li className={styles.tableRow} key={row}>
                                <p className={styles.tableRowText}>{row}</p>
                                <span className={styles.tableCell}>
                                    <Icon name='close' />
                                </span>
                                <span className={cx('tableCell', 'success')}>
                                    <Icon name='check' />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <Faq />
            <FeedbackListV2 />
            <div className={styles.storyIntro}>
                <h2 className={styles.storyTitle}>
                    Your <em>Life Story Plan</em> is ready!
                </h2>
                <RiskConcernSummaryV2 score={profileScore} chapterLabel={chapterLabel} spriteVersion='v51' />
            </div>
            {renderPlans()}
        </div>
    );
}

SaleV29.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.any,
    onExpirePromo: PropTypes.func,
    isExpired: PropTypes.bool,
    initialPrelander: PropTypes.string.isRequired,
    legalLinks: PropTypes.object.isRequired
};

export {
    SaleV29
};
